import type {StreamController} from './stream-controller';

export enum STREAM_STATUS {
  END = 'end',
  DATA = 'data',
  IDLE = 'idle',
  START = 'start',
  ERROR = 'error',
  CANCELLED = 'cancelled',
  IN_PROGRESS = 'inProgress',
}

export type StreamStatus = `${STREAM_STATUS}`;

export type StreamState = 'idle' | 'active' | 'ended' | 'error';

export interface StreamEvent<T = unknown> {
  name: string;
  data: T;
  run_id: string;
  tags?: string[];
  event: 'on_custom_event';
  metadata: StreamMetadata;
}

export interface StreamMetadata {
  thread_id: string;
  checkpoint_ns: string;
  langgraph_step: number;
  langgraph_node: string;
  [key: string]: unknown;
  langgraph_path: string[];
  __pregel_task_id: string;
  __pregel_resuming: boolean;
  langgraph_triggers: string[];
  langgraph_checkpoint_ns: string;
}

export interface StreamObserver<ChunkType = StreamEvent> {
  name: string;
  onError?: (err: unknown) => void;
  onEnd?: () => void | Promise<void>;
  onStart?: () => void | Promise<void>;
  onChunk: (chunk: ChunkType) => void | Promise<void>;
  setController(controller: StreamController<ChunkType> | undefined): void;
}

export interface IStreamTimer extends StreamObserver {
  getTotalStreamTime: () => number;
  /**
   * Returns an object keyed by node name containing each node's total execution time in milliseconds.
   */
  getNodeDurations: () => Record<string, number>;
  /**
   * Returns summary timing information for the entire stream and all tracked nodes.
   */
  getFinalTimingValues: () => Promise<{
    totalStreamTime: number;
    totalReasoningTime: number;
    nodeDurations: Record<string, number>;
  }>;
  formatTime: (time: number | undefined) => string;
}

export interface IStreamRecorder extends StreamObserver {
  getFilePath: () => string;
}

export interface IStreamCollector extends StreamObserver {
  getCollectedData: () => Record<string, unknown>;
}

// Simplified: Single observer map interface
export interface ObserverMap {
  streamTimer?: IStreamTimer;
  streamRecorder?: IStreamRecorder;
  streamCollector?: IStreamCollector;
  [key: string]: StreamObserver | undefined;
}

export type ObserverMapKeys =
  | 'streamTimer'
  | 'streamCollector'
  | 'streamRecorder';

export interface RawStreamEvent {
  type: keyof typeof STREAM_STATUS;
  payload: unknown;
}

export interface StreamConfig {
  timeout?: number;
  delimiter?: string;
  retryDelay?: number;
  retryAttempts?: number;
  logger?: (msg: string, meta?: unknown) => void;
}

export class StreamError extends Error {
  constructor(
    message: string,
    public readonly code: string,
    public readonly context?: unknown
  ) {
    super(message);
    this.name = 'StreamError';
    if (Error.captureStackTrace) {
      Error.captureStackTrace(this, StreamError);
    }
  }
}
